import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { AudioLines, Loader2, Trash2 } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { useConfirm } from '@orisini/ui/components/confirm'
import { contentApi, formatDuration, uploadAsset, type AudioItem } from '../../content/lib/content'

/**
 * Pustaka audio akun: backsound & SFX khas merek, dipakai otomatis di setiap
 * video akun ini kecuali brief di Create membawa Assets sendiri.
 */
export function AudioLibrary({ workspaceId }: { workspaceId: string }) {
  const confirm = useConfirm()
  const [items, setItems] = useState<AudioItem[] | null>(null)
  const [role, setRole] = useState<'backsound' | 'sfx'>('backsound')
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState<number | null>(null)

  const muat = useCallback(async () => {
    setItems(await contentApi.audioLibrary(workspaceId).catch(() => []))
  }, [workspaceId])
  useEffect(() => void muat(), [muat])

  const unggah = async (file: File | undefined) => {
    if (!file) return
    setBusy(0)
    try {
      const a = await uploadAsset(file, setBusy)
      await contentApi.addAudio(workspaceId, { assetId: a.id, role, label: label.trim() || file.name.replace(/\.[^.]+$/, '').slice(0, 40) })
      setLabel('')
      await muat()
      toast.success(role === 'backsound' ? 'Background music added' : 'Sound effect added')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't add the audio")
    } finally {
      setBusy(null)
    }
  }

  const hapus = async (it: AudioItem) => {
    if (!(await confirm({ title: `Remove "${it.label || it.asset.filename}"?`, description: 'Videos already made keep their sound.', confirmLabel: 'Remove', destructive: true }))) return
    await contentApi.removeAudio(workspaceId, it.id).catch(() => undefined)
    await muat()
  }

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Audio library</h3>
        <p className="text-xs text-muted-foreground">
          Music and sound effects every video of this project uses, unless a brief brings its own. The first background music is used; sound effects go where their label says (whoosh on scene changes, intro at the start). Only upload audio you have the right to use.
        </p>
      </div>
      {items === null ? (
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      ) : (
        items.length > 0 && (
          <Card className="divide-y divide-border">
            {items.map((it) => (
              <div key={it.id} className="flex items-center gap-3 p-3 text-sm">
                <span className="w-32 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">{it.role === 'backsound' ? 'Background music' : 'Sound effect'}</span>
                <span className="min-w-0 flex-1 truncate">{it.label || it.asset.filename}</span>
                <span className="tabular-nums text-xs text-muted-foreground">{formatDuration((it.asset.durationMs ?? 0) / 1000)}</span>
                <audio controls preload="none" src={it.asset.url} className="h-8 w-48" />
                <Button size="sm" variant="ghost" onClick={() => void hapus(it)} aria-label="Remove">
                  <Trash2 />
                </Button>
              </div>
            ))}
          </Card>
        )
      )}
      <div className="flex flex-wrap items-center gap-2">
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={role} onChange={(e) => setRole(e.target.value as 'backsound' | 'sfx')} aria-label="Type">
          <option value="backsound">Background music</option>
          <option value="sfx">Sound effect</option>
        </select>
        <Input className="h-9 w-48" value={label} maxLength={40} placeholder={role === 'sfx' ? 'whoosh, ding, intro…' : 'Label (optional)'} onChange={(e) => setLabel(e.target.value)} aria-label="Label" />
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:border-foreground/40">
          {busy !== null ? <Loader2 className="size-4 animate-spin" /> : <AudioLines className="size-4" />}
          {busy !== null ? `Uploading… ${Math.round(busy * 100)}%` : 'Upload audio'}
          <input
            type="file"
            accept="audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,.mp3,.wav,.m4a"
            className="hidden"
            disabled={busy !== null}
            onChange={(e) => {
              void unggah(e.target.files?.[0])
              e.target.value = ''
            }}
          />
        </label>
      </div>
    </section>
  )
}
